import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, AlertTriangle, DollarSign, Flag, ShieldOff, HelpCircle } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";
import { FinalCTA } from "@/components/home/HomeSections";

const HONEST_BREADCRUMBS = [
  { name: "Home", path: "/" },
  { name: "Honest Answers" },
];

export const Route = createFileRoute("/honest-answers")({
  head: () => ({
    meta: [
        { name: "description", content: "Brutally honest answers to the Colorado small-business marketing questions agencies usually dodge. Pricing, timelines, AI search, and what actually works." },
      {
        title: "Honest Answers | Real Colorado Marketing Advice",
      },
      {
        name: "description",
        content:
          "Plain-English answers to the questions most Colorado agencies won't touch — pricing, red flags, when to skip SEO, and when we're not the right fit.",
      },
      {
        property: "og:title",
        content: "Honest Answers | Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "Real talk about web design, SEO, and agency pricing in Colorado — including when you shouldn't hire us.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/honest-answers" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/honest-answers" }],
    scripts: [
      breadcrumbScriptTag(HONEST_BREADCRUMBS),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": "https://coloradowebimpressions.com/honest-answers#article",
          headline: "Honest Answers from a Colorado Marketing Agency",
          description:
            "Plain-English answers to the questions most Colorado agencies won't touch — pricing, red flags, when to skip SEO, and when we're not the right fit.",
          url: "https://coloradowebimpressions.com/honest-answers",
          image: "https://coloradowebimpressions.com/logo.png",
          datePublished: "2025-09-01",
          dateModified: "2026-05-25",
          author: { "@id": "https://coloradowebimpressions.com/meet-chris#chris-heidlebaugh" },
          publisher: { "@id": "https://coloradowebimpressions.com/#org" },
          mainEntityOfPage: "https://coloradowebimpressions.com/honest-answers",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://coloradowebimpressions.com/honest-answers#faq",
          mainEntity: [
            { "@type": "Question", name: "How much does a website really cost for a Colorado home-service business?", acceptedAnswer: { "@type": "Answer", text: "Real ranges depend on page count, copywriting, and integrations — most small-business sites land $4,500–$12,000 one-time. The full breakdown lives in our pricing guide." } },
            { "@type": "Question", name: "What are the biggest red flags when hiring a Colorado SEO or web design company?", acceptedAnswer: { "@type": "Answer", text: "Long contracts with early-termination fees, refusing to publish pricing, hosting your site on a proprietary platform, and 'guaranteed #1 rankings' are the four biggest tells. If you see any of them, walk away." } },
            { "@type": "Question", name: "When is Colorado Web Impressions NOT the best fit?", acceptedAnswer: { "@type": "Answer", text: "If you need a 50-state e-commerce push, a venture-funded growth team, or someone willing to lock you into a 24-month contract — we're not your shop. We focus on Colorado small and mid-sized businesses." } },
          ],
        }),
      },
    ],
  }),
  component: HonestAnswersHub,
});

const articles = [
  {
    slug: "/honest-answers/website-cost",
    icon: DollarSign,
    eyebrow: "Pricing",
    title: "How much does a website really cost for a Colorado home-service business?",
    desc: "Real ranges, what drives the price up or down, and what you should actually expect to pay in 2026.",
    read: "9 min read",
  },
  {
    slug: "/honest-answers/red-flags",
    icon: Flag,
    eyebrow: "Buyer's Guide",
    title: "10 red flags when hiring a Colorado SEO or web design company",
    desc: "If your agency does any of these — run. Built from 20+ years of cleaning up other people's messes.",
    read: "8 min read",
  },
  {
    slug: "/honest-answers/not-a-fit",
    icon: ShieldOff,
    eyebrow: "Disqualifier",
    title: "When Colorado Web Impressions is NOT the best fit for your business",
    desc: "We turn down work every month. Here's exactly when we'll tell you to hire someone else.",
    read: "6 min read",
  },
];

function HonestAnswersHub() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Honest Answers from a Colorado Marketing Agency"
        title={
          <>
            Real answers to questions{" "}
            <span className="text-gradient-gold">most agencies dodge</span>
          </>
        }
        description="No sales pitch. No fluff. Just plain-English answers about what marketing actually costs, when it works, when it doesn't, and how to spot a bad agency before you sign a check."
      >
        <div className="mb-6 flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Honest Answers" }]} />
        </div>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="#articles"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Read the articles
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </a>
          <Link
            to="/are-we-a-fit"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
          >
            Take the 2-min fit quiz
          </Link>
        </div>
      </PageHero>

      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-7 sm:p-9">
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-foreground sm:text-2xl">
                  Why publish this stuff?
                </h2>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">
                  Most agencies bury pricing, hide their bad fits, and never warn you about the
                  industry's tricks — because telling the truth costs them deals. We&apos;d rather
                  lose a bad-fit client up front than waste your money for six months and burn the
                  relationship. If reading these articles convinces you not to hire us, that&apos;s
                  a win for both of us.
                </p>
                <p className="mt-4 text-sm font-semibold uppercase tracking-wider text-brand-gold-deep">
                  — Chris Heidlebaugh, Owner
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="articles" className="bg-section-alt py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              The Articles
            </span>
            <h2 className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl">
              Start here. Read what applies. <span className="text-gradient-gold">Skip the rest.</span>
            </h2>
          </div>

          <div className="mt-14 grid gap-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={a.slug}
                className="group flex flex-col rounded-2xl border border-border bg-card p-7 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold-deep transition-smooth group-hover:bg-gradient-gold group-hover:text-brand-black">
                  <a.icon className="h-6 w-6" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  {a.eyebrow}
                </span>
                <h3 className="mt-2 flex-1 text-lg font-bold leading-snug text-foreground">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.desc}</p>
                <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                  <span className="text-xs font-semibold text-muted-foreground">{a.read}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                    Read article
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mx-auto mt-14 max-w-3xl rounded-2xl border border-border bg-card p-7 text-center shadow-card sm:p-9">
            <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black">
              <HelpCircle className="h-6 w-6" />
            </div>
            <h3 className="text-2xl font-extrabold text-foreground">
              Not sure what you actually need?
            </h3>
            <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
              Take our 2-minute fit quiz. We&apos;ll tell you straight whether we&apos;re a good
              match — and if not, exactly what you should do instead.
            </p>
            <Link
              to="/are-we-a-fit"
              className="group mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Start the fit quiz
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
